<script lang="ts">
import type { ShortcutJs } from "@/generated/models";
import { toTypedSchema } from "@vee-validate/valibot";
import { Form, FormControl, FormField, FormItem, FormMessage, FormMessageServer, useForm } from "ls/components/form";
import { hasProtocol, withProtocol } from "ufo";
import * as v from "valibot";
import { useTemplateRef } from "vue";
import EmbedLinkSearch from "./EmbedLinkSearch.vue";
import { useSetLinkEditContextSubmitHandler } from "./useSetLinkEditContextSubmitHandler";

const Schema = v.object({
  url: v.pipe(
    v.string(),
    v.trim(),
  ),
});
</script>

<script setup lang="ts">
defineProps<{
  shortcut: string;
  filterFiles?: (file: ShortcutJs) => boolean;
}>();

const emits = defineEmits<{
  submit: [string];
  cancel: [];
}>();

const model = defineModel<string>();

const { handleSubmit } = useForm({
  validationSchema: toTypedSchema(Schema),
  initialValues: {
    url: model.value ?? "",
  },
});

const onSubmit = handleSubmit(values => {
  const url = ensureProtocol(values.url);
  if (!isValidUrl(url)) return;
  model.value = url;
  emits("submit", url);
});

useSetLinkEditContextSubmitHandler(onSubmit);

function ensureProtocol(value: string) {
  let url = value;
  if (!hasProtocol(url)) {
    url = withProtocol(url, "https://");
  }
  return url;
}

function isValidUrl(value: string) {
  if (!value) return false;
  return URL.canParse(value);
}

const embedLinkSearch = useTemplateRef("embedLinkSearch");
function focus() {
  embedLinkSearch.value?.focus();
}
defineExpose({
  focus,
});
</script>

<template>
  <div
    class="flex min-h-0 w-full min-w-0 flex-col gap-3 p-1"
  >
    <slot name="title">
      <div class="pointer-events-none flex flex-col gap-1">
        <div class="text-sm/6 font-semibold text-balance">
          Embed link
        </div>
      </div>
    </slot>

    <div class="flex min-h-0 flex-col">
      <Form class="flex min-h-0 flex-col" @submit="onSubmit">
        <FormField v-slot="{ componentField }" class="flex min-h-0" name="url">
          <FormItem class="flex min-h-0 flex-col">
            <FormControl>
              <EmbedLinkSearch
                ref="embedLinkSearch"
                v-bind="componentField"
                v-model="model"
                :filter-files="filterFiles"
                :shortcut="shortcut"
                @submit="onSubmit()"
                @cancel="emits('cancel')"
              >
                <template #error>
                  <slot name="error">
                    <FormMessage />
                  </slot>
                </template>
              </EmbedLinkSearch>
            </FormControl>
          </FormItem>
        </FormField>
        <FormMessageServer />
      </Form>
    </div>
  </div>
</template>
